import { arrayMoveImmutable } from 'array-move';
import * as React from 'react';
import { useUpdateEffect } from 'react-use';

import { TrendingSelectionCard } from '../TrendingCard/TrendingSelectionCard';
import {
  SelectionFilterModel,
  TrendingSelectionModal,
  TrendingSelectionModalProps,
} from './TrendingSelectionModal';

const MAX_SELECTION = 3;

const reducer = (selectionList, action) => {
  switch (action.type) {
    case 'add':
      return [...selectionList, action.payload];
    case 'remove':
      return selectionList.filter((selectedItem) => action.payload.id !== selectedItem.id);
    case 'replace-one':
      return [action.payload];
    case 'addList':
      return action.selectionList;
    default:
      return selectionList;
  }
};

export interface TrendingMixSelectionModalProps extends TrendingSelectionModalProps {
  loading?: boolean;
  onApply?: (selectedTrendingList?: string[]) => void;
  selectedData?: any;
  maxSelection?: number;
  selectionIdField?: string | string[];
}

export const TrendingMixSelectionModal = (props: TrendingMixSelectionModalProps) => {
  const {
    renderCard,
    data = [],
    selectedData = [],
    loading = false,
    onSearch,
    onApply,
    maxSelection = MAX_SELECTION,
    paginationElement,
    selectionFilters: mixSelectionFilters = [],
    selectionIdField = 'id',
    onFilterSelect,
    visible,
    ...rest
  } = props;

  const [selectionList, setSelectionList] = React.useReducer(reducer, selectedData);
  const [isSelectedFilter, setIsSelectedFilter] = React.useState(false);

  const getMappedData = React.useCallback(
    (inputData) => {
      return inputData.map((item) => {
        if (Array.isArray(selectionIdField)) {
          const { id: referenceId, ...restItem } = item;
          const id = selectionIdField.reduce((obj, field) => obj[field], item);
          return {
            ...restItem,
            id,
            referenceId,
          };
        }

        if (selectionIdField !== 'id') {
          const { id: referenceId, ...restItem } = item;

          return {
            ...restItem,
            id: item[selectionIdField],
            referenceId,
          };
        }
        return item;
      });
    },
    [selectionIdField]
  );

  const mappedData = React.useMemo(() => {
    return getMappedData(data);
  }, [data, getMappedData]);

  const mappedSelectedData = React.useMemo(() => {
    return getMappedData(selectedData);
  }, [getMappedData, selectedData]);

  useUpdateEffect(() => {
    if (mappedSelectedData.length) {
      setSelectionList({ type: 'addList', selectionList: mappedSelectedData });
    }
  }, [mappedSelectedData.length, visible]);

  const selectionFilters: SelectionFilterModel[] = React.useMemo(() => {
    return [
      ...mixSelectionFilters,
      {
        key: 'SELECTED',
        group: 'main',
        render: () => `Selected (${selectionList.length || 0}/${maxSelection})`,
      },
    ];
  }, [maxSelection, mixSelectionFilters, selectionList.length]);

  const renderSelectionCard = React.useCallback(
    (cardData) => {
      const handleSelect = (previousSelection) => {
        // Select for 1 max-selection -> replace new value
        if (maxSelection === 1) {
          setSelectionList({ type: 'replace-one', payload: cardData });
          return;
        }

        // Select for multiple selection -> stop add select when user exceed max selection
        if (previousSelection) {
          setSelectionList({ type: 'remove', payload: cardData });
        } else if (selectionList.length < maxSelection) {
          setSelectionList({ type: 'add', payload: cardData });
        }
      };

      const currentSelectionIndex = selectionList.findIndex(
        (selectedItem) => cardData?.id === selectedItem.id
      );

      const selected = currentSelectionIndex > -1;
      const selectionNumber = (currentSelectionIndex || 0) + 1;
      return (
        <TrendingSelectionCard
          onSelected={handleSelect}
          selected={selected}
          selectionNumber={selectionNumber}
        >
          {renderCard(cardData)}
        </TrendingSelectionCard>
      );
    },
    [maxSelection, renderCard, selectionList]
  );

  const getSortSelectionList = (oldIndex, newIndex) => {
    return arrayMoveImmutable([].concat(selectionList), oldIndex, newIndex).filter((el) => !!el);
  };

  const handleSortEnd = (sort) => {
    const { oldIndex, newIndex } = sort;
    const newSelectionList = getSortSelectionList(oldIndex, newIndex);
    setSelectionList({ type: 'addList', selectionList: newSelectionList });
  };

  return (
    <TrendingSelectionModal
      loading={loading}
      data={isSelectedFilter ? selectionList : mappedData}
      searchBoxDisabled={isSelectedFilter}
      sortable={isSelectedFilter}
      applyButtonDisabled={selectionList.length < maxSelection}
      paginationElement={!isSelectedFilter && paginationElement}
      renderCard={renderSelectionCard}
      selectionFilters={selectionFilters}
      onApply={() => {
        const selectionListId = selectionList.map(({ id }) => id);
        onApply?.(selectionListId);
      }}
      onSortEnd={handleSortEnd}
      onSearch={onSearch}
      onFilterSelect={(key) => {
        setIsSelectedFilter(key === 'SELECTED');
        onFilterSelect?.(key);
      }}
      visible={visible}
      {...rest}
    />
  );
};
